import { Controller, Control, FieldErrors, FieldValues, Path } from 'react-hook-form';
import { TextField, TextFieldProps } from '@mui/material';

const toDigits = (raw: string) => (raw ? raw.replace(/\D/g, '') : '');

const formatByGroups = (digitsOnly: string, grouping: number[]) => {
    const parts: string[] = [];
    let i = 0;
    grouping.forEach((g) => {
        if (i < digitsOnly.length) {
            parts.push(digitsOnly.slice(i, i + g));
            i += g;
        }
    });
    return parts.join(' ').trim();
};

type NumberPhoneProps<TFieldValues extends FieldValues> = {
    name: Path<TFieldValues>;
    control: Control<any>;
    label?: string;
    errors?: FieldErrors<TFieldValues>;
    defaultValue?: string;
    helperText?: string;
    grouping?: number[];
} & Omit<TextFieldProps, 'name' | 'defaultValue' | 'helperText' | 'type' | 'value' | 'onChange'>;

export default function NumberPhone<TFieldValues extends FieldValues>({
    name,
    control,
    label,
    errors,
    defaultValue = '',
    helperText,
    grouping = [3, 4, 4],
    ...rest
}: NumberPhoneProps<TFieldValues>) {
    const maxDigits = grouping.reduce((a, b) => a + b, 0);
    const errorObj = errors && (errors as any)[name];
    const errorMsg = errorObj?.message as string | undefined;

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={toDigits(defaultValue) as any}
            render={({ field }) => {
                const cleanValue = toDigits(field.value ?? '');
                const displayValue = formatByGroups(cleanValue, grouping);

                return (
                    <TextField
                        {...rest}
                        label={label}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        error={!!errorMsg}
                        helperText={helperText ?? errorMsg ?? ''}
                        value={displayValue}
                        onChange={(e) => {
                            const digits = toDigits(e.target.value).slice(0, maxDigits);
                            field.onChange(digits);
                        }}
                        onPaste={(e) => {
                            e.preventDefault();
                            const text = (e.clipboardData || (window as any).clipboardData).getData('text');
                            const digits = toDigits(text).slice(0, maxDigits);
                            field.onChange(digits);
                        }}
                        onKeyDown={(e) => {
                            const allowKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Home',
                                'End',
                                'Tab',
                                'Enter',
                            ];
                            if (allowKeys.includes(e.key)) return;
                            if (/^\d$/.test(e.key)) return;
                            e.preventDefault();
                        }}
                    />
                );
            }}
        />
    );
}
